import { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Input } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import {
    useAddClientStaff,
    type ClientRecord,
    type ClientStaffStatus,
} from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import { toIsoDate } from '@/lib/calendar';
import { SelectField } from './SelectField';

function oneYearFromTodayIso(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return toIsoDate(d);
}

/*
 * Assigns a staff member (any practice user) to a client. The userId
 * is the FK that drives the `view_assigned_clients` permission, so the
 * dropdown lists users from the practice directory — external
 * contractors without user accounts can't be assigned. `role` is free
 * text (whatever label the practice wants — "Primary RBT", "Coverage
 * RBT", "Lead BCBA"); edits are modelled as remove-then-add because
 * the display shape is cheap to recreate.
 *
 * The BCBA and anyone already on the staff array is filtered out —
 * re-adding them would just produce a duplicate row.
 */

interface AssignStaffDialogProps {
    client: ClientRecord;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const STAFF_STATUSES: ClientStaffStatus[] = ['Active', 'Inactive'];

type FormState = {
    userId: string;
    role: string;
    start: string;
    status: ClientStaffStatus;
};

const EMPTY_FORM: FormState = {
    userId: '',
    role: '',
    start: '',
    status: 'Active',
};

export function AssignStaffDialog({ client, open, onOpenChange }: AssignStaffDialogProps) {
    const addMut = useAddClientStaff(client.id);
    const directory = useUserDirectory();
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [fieldError, setFieldError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setForm(EMPTY_FORM);
            setFieldError(null);
            addMut.reset();
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Filter out the supervising BCBA and anyone already on the staff
    // array — re-adding would just duplicate a row.
    const alreadyAssigned = useMemo(() => {
        const ids = new Set<string>();
        if (client.bcbaId) ids.add(client.bcbaId);
        for (const s of client.staff || []) {
            if (s.userId) ids.add(s.userId);
        }
        return ids;
    }, [client.bcbaId, client.staff]);

    const options = useMemo(() => {
        const list = (directory.data || []).filter((u) => !alreadyAssigned.has(u.id));
        list.sort((a, b) => {
            const la = a.last.toLowerCase();
            const lb = b.last.toLowerCase();
            if (la !== lb) return la < lb ? -1 : 1;
            return a.first.toLowerCase() < b.first.toLowerCase() ? -1 : 1;
        });
        return list;
    }, [directory.data, alreadyAssigned]);

    function update<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm((f) => ({ ...f, [key]: value }));
    }

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!form.userId) {
            setFieldError('Select a staff member.');
            return;
        }
        if (form.start && !/^\d{4}-\d{2}-\d{2}$/.test(form.start)) {
            setFieldError('Enter a valid start date or leave it blank.');
            return;
        }
        setFieldError(null);
        try {
            await addMut.mutateAsync({
                userId: form.userId,
                role: form.role.trim(),
                start: form.start,
                status: form.status,
            });
            onOpenChange(false);
        } catch {
            /* surfaced below */
        }
    }

    const serverError = (() => {
        const e = addMut.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to assign staff';
        return 'Unexpected error';
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Assign Staff</DialogTitle>
                    <DialogDescription>
                        Staff can only be assigned from the practice directory — assignments
                        drive who sees this client.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} noValidate>
                    {(serverError || fieldError) && (
                        <Alert variant="danger" className="mb-4">
                            {fieldError ?? serverError}
                        </Alert>
                    )}

                    <FormField label="Staff member" required>
                        {(id) => (
                            <SelectField
                                id={id}
                                value={form.userId}
                                onChange={(e) => update('userId', e.target.value)}
                                disabled={directory.isLoading}
                            >
                                <option value="">
                                    {directory.isLoading ? 'Loading…' : 'Select a staff member…'}
                                </option>
                                {options.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.last}, {u.first} — {u.roleName}
                                    </option>
                                ))}
                            </SelectField>
                        )}
                    </FormField>

                    <FormField label="Role" hint="e.g. Primary RBT, Coverage RBT, Lead BCBA">
                        {(id) => (
                            <Input
                                id={id}
                                value={form.role}
                                onChange={(e) => update('role', e.target.value)}
                                placeholder="Role on this client"
                            />
                        )}
                    </FormField>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        <FormField label="Start date">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="date"
                                    min="1900-01-01"
                                    max={oneYearFromTodayIso()}
                                    value={form.start}
                                    onChange={(e) => update('start', e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Status">
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={form.status}
                                    onChange={(e) => update('status', e.target.value as ClientStaffStatus)}
                                >
                                    {STAFF_STATUSES.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={addMut.isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" variant="success" disabled={addMut.isPending}>
                            {addMut.isPending ? 'Assigning…' : 'Assign'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
